import type React from "react";
import type { TemplateNode } from "../types/template";
import { resolveVariablePath } from "./templateVariableHelpers";
import { formatMoney } from "@/lib/money";

/**
 * Deeply resolve a path like "menu.categories[0].items[2].name" from an object.
 */
export function resolvePath(obj: Record<string, unknown> | null | undefined, path: string): unknown {
  if (!obj || !path) return undefined;
  return path.split(".").reduce<unknown>((acc, part) => {
    if (acc == null) return undefined;
    const match = part.match(/^(\w+)\[(\d+)\]$/);
    if (match) {
      const [, key, index] = match;
      return ((acc as Record<string, unknown>)?.[key] as unknown[] | undefined)?.[Number(index)];
    }
    return (acc as Record<string, unknown>)[part];
  }, obj);
}

/**
 * Replace all {expr} bindings in a string with values from context.
 * Example: "Hello {menu.title}" => "Hello Curry Leaf"
 */
export function resolveBindings(str: string, context: Record<string, unknown>): string {
  if (!str || typeof str !== "string") return str;
  return str.replace(/\{([\w.[\]]+)\}/g, (_, expr) => {
    const resolve = resolvePath(context, expr.trim());
    const resolved = resolveBindingExpression(resolve, context);
    return resolved == null ? "" : String(resolved);
  });
}

/**
 * Recursively resolve all string bindings in an object (props, style, etc).
 * Returns a deep clone with all bindings replaced.
 */
export function resolveAllBindings(obj: unknown, context: Record<string, unknown>): unknown {
  if (typeof obj === "string") {
    return resolveBindings(obj, context);
  } 
  if (Array.isArray(obj)) {
    return obj.map(v => resolveAllBindings(v, context));
  }
  if (obj && typeof obj === "object") {
    const out: Record<string, unknown> = {};
    for (const k in obj as Record<string, unknown>) {
      out[k] = resolveAllBindings((obj as Record<string, unknown>)[k], context);
    }
    return out;
  }
  return obj;
}

export function isBindingMap(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

export function hasRenderableValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

export function pickRenderableText(...values: unknown[]): string {
  for (const value of values) {
    if (hasRenderableValue(value)) {
      return typeof value === "string" ? value : String(value);
    }
  }
  return "";
}

export function renderTextOrChildren(text: unknown, children: unknown): React.ReactNode {
  return (hasRenderableValue(text) ? text : children) as React.ReactNode;
}

export function resolveDirectValue(value: unknown, context: Record<string, unknown>): unknown {
  if (typeof value !== "string") return value;

  if (value.includes("${")) {
    const variables: Record<string, unknown> =
      (context?.templateResolvedVariables as Record<string, unknown> | undefined) ||
      ((context?.template as Record<string, unknown>)?.variables as Record<string, unknown> | undefined) ||
      {};
    return resolveTemplateTokenString(value, variables);
  }

  if (value.includes("{")) {
    return resolveBindingExpression(value, context);
  }

  if (/^[a-zA-Z_$][\w$]*(?:\[[0-9]+\]|\.[a-zA-Z_$][\w$]*)*$/.test(value.trim())) {
    const resolved = resolvePath(context, value.trim());
    return resolved === undefined ? value : resolved;
  }

  return value;
}

export function resolveRenderableValue(value: unknown, context: Record<string, unknown>): unknown {
  const resolved = resolveDirectValue(value, context);
  if (typeof resolved === "string" && /{[^}]+}/.test(resolved)) {
    const rebound = resolveBindingExpression(resolved, context);
    return rebound || resolved;
  }
  return resolved;
}


const FIELD_BINDING_MAP: Record<string, { helper: string; label?: string }> = {
  variants: { helper: "formatVariants", label: "Variants: " },
  nutritional_info: { helper: "formatNutritionalInfo", label: "Nutritional Info: " },
  tags: { helper: "formatTags", label: "Tags: " },
  created_at: { helper: "formatCreatedAt", label: "Created: " },
  price: { helper: "formatPrice", label: "Price: " },
  price_range: { helper: "formatPriceRange", label: "Price: " },
  price_dual: { helper: "formatPriceDual", label: "Price: " },
  contact: { helper: "formatContact", label: "" },
  socials: { helper: "formatSocialLinks", label: "" },
  social_links: { helper: "formatSocialLinks", label: "" },
  availability: { helper: "formatAvailability", label: "" },
  availability_window: { helper: "formatAvailabilityWindow", label: "" },
  category: { helper: "formatCategory", label: "Category: " },
  promo: { helper: "promoLabel", label: "" },
  custom_label: { helper: "custom_label", label: "" },
  
  // Add more as needed
};

function formatAvailabilityDays(days?: string[]) {
  if (!Array.isArray(days) || days.length === 0) return "";
  return days.join(", ");
}

function formatAvailabilityDateLabel(value?: string | null) {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatAvailabilityDateRange(from?: string | null, until?: string | null) {
  if (!from && !until) return "";
  if (from && until) {
    return `${formatAvailabilityDateLabel(from)} → ${formatAvailabilityDateLabel(until)}`;
  }
  if (from) return `From ${formatAvailabilityDateLabel(from)}`;
  return `Until ${formatAvailabilityDateLabel(until)}`;
}

function formatAvailabilityTimeRange(range?: { start?: string; end?: string }) {
  if (!range?.start && !range?.end) return "";
  if (range?.start && range?.end) return `${range.start} - ${range.end}`;
  return range?.start || range?.end || "";
}

export function buildAvailabilityWindow(item: unknown) {
  const obj = item as Record<string, unknown> | null | undefined;
  const dateRange = formatAvailabilityDateRange(obj?.available_from as string | null | undefined, obj?.available_until as string | null | undefined);
  const days = formatAvailabilityDays(obj?.available_Days as string[] | undefined);
  const timeRange = formatAvailabilityTimeRange(obj?.available_Times as { start?: string; end?: string } | undefined);
  const parts = [dateRange, days, timeRange].filter(Boolean);
  return parts.join(" • ");
}

export const bindingHelpers: {
  [key: string]: (...args: unknown[]) => unknown;
  promoLabel(promo: unknown, item: unknown): string;
  custom_label(promo: unknown, item: unknown): string;
  formatVariants(variants: unknown): string;
  formatNutritionalInfo(nutritional_info: unknown): string;
  formatTags(tags: unknown): string;
  formatCreatedAt(date: unknown): string;
  formatPrice(item: unknown): string;
  formatPriceRange(item: unknown): string;
  formatPriceDual(item: unknown): string;
  formatPriceVariant(item: unknown, variant?: unknown): string;
  formatContact(business: unknown): string;
  formatSocialLinks(business: unknown): string;
  formatAvailability(item: unknown): string;
  formatAvailabilityWindow(item: unknown): string;
  formatCategory(category: unknown): string;
} = {
  // A promotion is a DISCOUNT. This used to print "+20%" and "+₵8" on every
  // template badge, so a guest read an offer as a surcharge. The sign came from
  // `p.trend`, which is not a column on `promotions` and appears nowhere else in
  // the codebase -- always undefined, so the ternary always fell through to '+'.
  // The wording now matches formatPromotionDiscountLabel() in
  // lib/promotions/presentation.ts, which is what the rest of the app shows.
  promoLabel(promo: unknown, item: unknown) {
    const p = promo as Record<string, unknown> | null | undefined;
    const i = item as Record<string, unknown> | null | undefined;
    if (!p || !p.is_active) return "";
    let value = "";
    switch (p.discount_type) {
      case "percentage":
        value = `${p.discount_value}% off`;
        break;
      case "fixed":
        // Was `${i?.currency}${p.discount_value} off` — the currency CODE glued
        // straight to the amount, so a GHS menu read "GHS15 off" with no symbol
        // and no rounding. formatMoney turns the code into the right symbol.
        value = `${formatMoney(Number(p.discount_value), (i?.currency as string) ?? undefined)} off`;
        break;
      case "bogo":
        value = "Buy 1 Get 1";
        break;
      case "custom":
        value = (p.description || p.custom_label || p.title || "") as string;
        break;
      default:
        value = "";
    }
    const label = (p.custom_label || p.title || p.discount_type || "") as string;
    if (!value) return label;
    return label ? `${label}: ${value}` : value;
  },

  // Byte-identical to promoLabel before this: two copies of the same formatter
  // meant the sign bug had to be found and fixed twice. Delegate instead.
  custom_label(promo: unknown, item: unknown) {
    return bindingHelpers.promoLabel(promo, item);
  },

  formatVariants(variants: unknown) {
    if (!variants) return "";
    if (Array.isArray(variants)) return variants.join(", ");
    if (typeof variants === "object" && variants !== null)
      return Object.entries(variants as Record<string, unknown>)
        .map(([k, v]) => `${k}: ${v}`)
        .join(", ");
    return String(variants);
  },

  formatNutritionalInfo(nutritional_info: unknown) {
    if (!nutritional_info) return "";
    if (typeof nutritional_info === "object" && nutritional_info !== null)
      return Object.entries(nutritional_info as Record<string, unknown>)
        .map(([k, v]) => `${k}: ${v}`)
        .join(", ");
    return String(nutritional_info);
  },

  formatTags(tags: unknown) {
    if (!tags || !Array.isArray(tags) || tags.length === 0) return "";
    return tags.join(", ");
  },

  formatCreatedAt(date: unknown) {
    if (!date) return "";
    const d = new Date(date as string);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleString();
  },

  formatPrice(item: unknown) {
    if (!item) return "";
    const i = item as Record<string, unknown>;
    return `${i.currency ?? ""}${i.price ?? ""}`;
  },

  // `range` and `dual` are pre-computed on the item by menuDataBuilder (doc 03
  // §3). Each falls back to the flat price so a price is never blank.
  formatPriceRange(item: unknown) {
    const i = item as Record<string, unknown> | null | undefined;
    if (!i) return "";
    return String(i.price_range ?? `${i.currency ?? ""}${i.price ?? ""}`);
  },

  formatPriceDual(item: unknown) {
    const i = item as Record<string, unknown> | null | undefined;
    if (!i) return "";
    return String(i.price_dual ?? `${i.currency ?? ""}${i.price ?? ""}`);
  },

  formatPriceVariant(item: unknown, variant: unknown) {
    const i = item as Record<string, unknown> | null | undefined;
    if (!i) return "";
    const mode = typeof variant === "string" ? variant : "single";
    if (mode === "range" && i.price_range) return String(i.price_range);
    if (mode === "dual" && i.price_dual) return String(i.price_dual);
    if (mode === "dash" && i.price_dash) return String(i.price_dash);
    if (mode === "code" && i.price_code) return String(i.price_code);
    if (mode === "short" && i.price_short) return String(i.price_short);
    return `${i.currency ?? ""}${i.price ?? ""}`;
  },

  // Footer contact block (doc 03 §3): phone · email · website, skipping blanks.
  formatContact(business: unknown) {
    const b = business as Record<string, unknown> | null | undefined;
    if (!b) return "";
    return [b.phone, b.email, b.website]
      .map((value) => (typeof value === "string" ? value.trim() : ""))
      .filter(Boolean)
      .join(" · ");
  },

  // Platform labels from `business.socialLinks`, de-duplicated and in order.
  formatSocialLinks(business: unknown) {
    const b = business as Record<string, unknown> | null | undefined;
    if (!b) return "";
    const links = Array.isArray(b.socialLinks) ? (b.socialLinks as Record<string, unknown>[]) : [];
    const labels = links
      .map((link) => {
        const platform = typeof link?.platform === "string" ? link.platform.trim() : "";
        return platform ? platform.charAt(0).toUpperCase() + platform.slice(1) : "";
      })
      .filter(Boolean);
    return [...new Set(labels)].join(" · ");
  },

  formatAvailability(item: unknown) {
    const i = item as Record<string, unknown> | null | undefined;
    const window = buildAvailabilityWindow(item);
    const avail =
      typeof i?.is_available === "boolean"
        ? i.is_available
        : typeof i?.available === "boolean"
          ? i.available
          : undefined;
    if (typeof avail === "boolean") {
      if (!avail) return "Unavailable";
      return window ? `Available • ${window}` : "Available";
    }
    return window || "";
  },

  formatAvailabilityWindow(item: unknown) {
    return buildAvailabilityWindow(item);
  },

  formatCategory(category: unknown) {
    return ((category as Record<string, unknown> | null | undefined)?.name as string) || ",";
  }
};


type BindingValue =
  | string
  | {
      field: string;
      label?: string;
      hideIfEmpty?: boolean;
      variant?: "single" | "range" | "dual" | string;
    };

/**
 * Resolves a binding value (string or {field}) using context and helper functions.
 * Supports secure evaluation and proper label handling.
 */
export function resolveBinding(binding: BindingValue, context: Record<string, unknown>): string {
  if (typeof binding === "string") {
    return resolveTemplateString(binding, context);
  }

  const c = context as Record<string, any>;
  let value: unknown = "";
  let label: string = "";

  if ("field" in binding) {
    const { field, label: customLabel = "", hideIfEmpty = true } = binding as { field: string; label?: string; hideIfEmpty?: boolean };
    const map = FIELD_BINDING_MAP[field];
    if (!map) return "";

    // Use correct helper and context for each field
    switch (map.helper) { 
      case "promoLabel":
        value = bindingHelpers.promoLabel(
          c.item?.promotion || c.category?.promotion || context.promo,
          c.item
        );
        break;
      case "custom_label":
        value = bindingHelpers.custom_label(
          c.item?.promotion || c.category?.promotion || context.promo,
          c.item
        );
        break;
      case "formatCategory":
        value = bindingHelpers.formatCategory(c.category);
        break;
      case "formatPrice":
        value = bindingHelpers.formatPriceVariant(c.item, (binding as { variant?: unknown }).variant);
        break;
      case "formatPriceRange":
      case "formatPriceDual":
      case "formatAvailability":
        value = bindingHelpers[map.helper](c.item);
        break;
      case "formatContact":
      case "formatSocialLinks":
        value = bindingHelpers[map.helper](c.business);
        break;
      case "formatCreatedAt":
        value = bindingHelpers.formatCreatedAt(c.item?.created_at);
        break;
      case "formatVariants":
        value = bindingHelpers.formatVariants(c.item?.variants);
        break;
      case "formatNutritionalInfo":
        value = bindingHelpers.formatNutritionalInfo(c.item?.nutritional_info);
        break;
      default:
        // Default: pass field value from item or context
        value = bindingHelpers[map.helper]?.(c.item?.[field] ?? context[field]);
        break;
    }
    label = customLabel || map.label || "";
    if (
      hideIfEmpty &&
      (value == null || value === "" || value === "Invalid Date")
    ) {
      return "";
    }
  } else if ("expr" in binding) {
    // Advanced: evaluate JS expression
    const { expr, label: leb = "", hideIfEmpty = true } = binding;
    label = leb;
    value = evaluateExpression(expr, context);
    if (
      hideIfEmpty &&
      (value == null || value === "" || value === "Invalid Date")
    ) {
      return "";
    }
  }

  if (value == null || value === "" || value === "Invalid Date") return "";
  // No console.log here for security/cleanliness in prod

  return `${label}${value}`;
}

function evaluateExpression(expr: string, context: Record<string, unknown>): unknown {
  return evaluateSafeExpression(expr, { ...bindingHelpers, ...context });
}

const MAX_TEMPLATE_EXPR_LENGTH = 220;
const UNSAFE_TEMPLATE_EXPR_PATTERN =
  /\b(?:window|document|globalThis|Function|eval|constructor|__proto__|prototype|import|require|process|fetch|XMLHttpRequest|WebSocket|localStorage|sessionStorage|navigator|history|location|cookie|atob|btoa)\b/i;

function isSafeTemplateExpression(expr: string, options?: { allowTemplateLiteral?: boolean }) {
  const trimmed = expr.trim();
  if (!trimmed) return false;
  if (trimmed.length > MAX_TEMPLATE_EXPR_LENGTH) return false;
  if (UNSAFE_TEMPLATE_EXPR_PATTERN.test(trimmed)) return false;
  if (/(^|[^.\w$])new\s+/i.test(trimmed)) return false;
  if (/=>|function\s*\(|class\s+/i.test(trimmed)) return false;
  if (/[\r\n;]/.test(trimmed)) return false;
  if (!options?.allowTemplateLiteral && trimmed.includes("`")) return false;
  // Block direct assignments while allowing comparisons (==, ===, <=, >=, !=, !==).
  if (/(^|[^=!<>])=([^=]|$)/.test(trimmed)) return false;
  return true;
}

export function evaluateSafeExpression(
  expr: string,
  scope: Record<string, unknown>,
  options?: { allowTemplateLiteral?: boolean },
): unknown {
  if (!isSafeTemplateExpression(expr, options)) {
    return "";
  }

  try {
    const keys = Object.keys(scope);
    const values = Object.values(scope);
    const fn = new Function(...keys, `"use strict"; return (${expr});`);
    return fn(...values);
  } catch {
    return "";
  }
}

function resolveTemplateString(str: string, context: Record<string, unknown>): string {
  let result = str.replace(/\{([^}]+)\}/g, (_, expr) => {
    const value = evaluateExpression(expr, context);
    return value == null ? "" : String(value);
  });
  result = result.replace(/\s*[·:\-–,|,;]\s*$/, '');
  return result;
}


/**
 * Resolve a binding string, supporting full JS expressions and template literals inside { ... }.
 * Hides the field if the result is empty, null, undefined, or "Invalid Date".
 */
export function resolveBindingExpression(str: unknown, context: Record<string, unknown>): string {
  if (!str || typeof str !== "string") return String(str ?? "");
  const resolved = str.replace(/\{([^}]+)\}/g, (_, expr) => {
    try {
      const result = evaluateSafeExpression(expr, { ...bindingHelpers, ...context }, {
        allowTemplateLiteral: true,
      });
      if (
        result == null ||
        result === "" ||
        (typeof result === "string" && result.trim() === "") ||
        result === "Invalid Date"
      ) {
        return "";
      }
      return String(result);
    } catch (e) {
      return "";
    }
  });

  // Audit ISSUE-16. A binding that resolves to nothing leaves its punctuation
  // behind: "{branch.name} : {tagline}" with no tagline rendered as
  // "Main Branch :" on the live guest menu. `resolveTemplateString` has stripped
  // this since it was written; this sibling never did, and the two are used
  // interchangeably by templates, so the same template text cleaned up or did
  // not depending on which resolver reached it.
  //
  // Trailing only, deliberately. Decoration *between* two empty bindings
  // ("{a} \u2605 / {b} \u2605") cannot be fixed by trimming an end — that needs
  // dropping whole segments, which changes rendering semantics for every
  // template and is a separate decision.
  return resolved.replace(/\s*[\u00b7:\-\u2013,|,;]\s*$/, '');
}


/**
 * For strings like "Label: {binding}", only show if binding resolves to a non-empty value.
 * Returns "" if binding is empty, otherwise returns "Label: value".
 */
export function resolveLabelBinding(str: string, context: Record<string, unknown>): string {
  if (!str || typeof str !== "string") return str;
  // Only match a single {expr} in the string
  const match = str.match(/^([\s\S]*?)\{([\s\S]+)\}([\s\S]*)$/);
  if (!match) return resolveBindingExpression(str, context);
  const [, label, expr, suffix] = match;
  const value = resolveBindingExpression(`{${expr}}`, context);
  if (!value || value === "undefined" || value === "null" || value === "Invalid Date") return "";
  return `${label}${value}${suffix}`;
}

export function resolveTemplateTokenString(value: string, variables: Record<string, unknown>): string {
  if (typeof value !== "string" || !value.includes("${")) return value;
  return value.replace(/\$\{([^}]+)\}/g, (_, expr) => {
    const resolved = resolveVariablePath(variables, expr.trim());
    return resolved != null ? String(resolved) : "";
  });
}

// --- Utility: Text Utilities (truncate, ellipsis, utils) ---
 /* Example:
 *   "bg-white {item.available ? 'bg-[#e6fbe6]' : 'bg-[#fdeaea]'} {item.available ? 'text-[#1a7f37]' : 'text-[#b00]'}"
 * Will evaluate each {...} using the provided context and helpers, and join the results.
 */
export function resolveClassName(className: string, context: Record<string, unknown>): string {
  if (!className || typeof className !== "string") return className;

  // Replace all { ... } expressions with their evaluated result
  const resolved = className.replace(/\{([^}]+)\}/g, (_, expr) => {
    const result = evaluateSafeExpression(expr, { ...bindingHelpers, ...context });
    return result == null ? "" : String(result);
  });

  // Collapse multiple spaces and trim
  return resolved.replace(/\s+/g, " ").trim();
}
